// web/src/components/Scoreline.tsx
// The match scoreline rendered at --mega weight, "vs Hardwick Lions" small
// caps below, with optional date/format/matchType meta line.
//
// On mount, the two numbers count up from 0 (animated via framer-motion).
// Falls back to "—" when scores aren't recorded.

import { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import type { CSSProperties } from 'react';

export interface ScorelineProps {
  ourScore?: number;
  theirScore?: number;
  opposition?: string;
  meta?: string;
  /** When true, skip the count-up animation (used in static previews). */
  staticRender?: boolean;
  /** When true, dark surface (white text). */
  onDark?: boolean;
  style?: CSSProperties;
}

function useCountUp(target: number | undefined, duration: number, enabled: boolean): number {
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (v: number) => Math.round(v));
  const [display, setDisplay] = useState<number>(enabled ? 0 : target ?? 0);

  useEffect(() => {
    if (typeof target !== 'number') return;
    if (!enabled) {
      setDisplay(target);
      return;
    }
    const controls = animate(motionValue, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],  // --ease-editorial
    });
    const unsubscribe = rounded.on('change', (v: number) => setDisplay(v));
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [target, duration, enabled, motionValue, rounded]);

  return display;
}

export function Scoreline({
  ourScore,
  theirScore,
  opposition,
  meta,
  staticRender = false,
  onDark = false,
  style,
}: ScorelineProps) {
  const animate = !staticRender;
  const us = useCountUp(ourScore, 0.9, animate);
  const them = useCountUp(theirScore, 0.9, animate);

  const haveScores = typeof ourScore === 'number' && typeof theirScore === 'number';
  const inkPrimary = onDark ? 'var(--ink-on-green)' : 'var(--ink)';
  const inkMuted = onDark ? 'rgba(250,245,236,0.7)' : 'var(--muted)';

  return (
    <div style={{ textAlign: 'center', ...style }}>
      <motion.div
        initial={animate ? { opacity: 0, y: 8 } : undefined}
        animate={animate ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="mfj-tnum"
        style={{
          fontFamily: 'var(--font-masthead)',
          fontSize: 'var(--mega)',
          lineHeight: 1,
          color: inkPrimary,
          letterSpacing: '-0.02em',
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          gap: 'var(--space-md)',
        }}
      >
        <span>{haveScores ? us : '—'}</span>
        <span style={{ color: inkMuted, fontSize: '0.75em' }}>–</span>
        <span>{haveScores ? them : '—'}</span>
      </motion.div>

      {opposition ? (
        <div
          style={{
            marginTop: 'var(--space-sm)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--small)',
            color: inkMuted,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}
        >
          vs {opposition}
        </div>
      ) : null}

      {meta ? (
        <div
          style={{
            marginTop: 'var(--space-xs)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--micro)',
            color: inkMuted,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {meta}
        </div>
      ) : null}
    </div>
  );
}
